import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight, Waves } from "lucide-react";

const SITE = "https://spa-report-hub.emergent.host";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";

  useSEO({
    title: "Blog DoctoSpa — Guides diagnostic, entretien & pannes de spa",
    description:
      "Guides pratiques, décryptage des codes erreur (Balboa, Gecko) et conseils d'entretien pour votre spa. Signé par des techniciens certifiés.",
    url: `${SITE}/blog`,
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "DoctoSpa — Blog technique",
      url: `${SITE}/blog`,
      publisher: { "@type": "Organization", name: "DoctoSpa", url: SITE },
    },
  });

  useEffect(() => {
    setLoading(true);
    const params = activeCategory ? { category: activeCategory } : {};
    Promise.all([
      api.get("/blog/posts", { params }),
      api.get("/blog/categories"),
    ])
      .then(([p, c]) => {
        setPosts(p.data.posts || []);
        setCategories(c.data.categories || []);
      })
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-20" data-testid="blog-page">
      <div className="max-w-2xl">
        <div className="eyebrow">Blog technique</div>
        <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
          Diagnostiquer,<br />
          <span className="text-primary">réparer</span>, entretenir.
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Guides pratiques rédigés par notre équipe technique — codes erreur, chimie de l'eau,
          pannes fréquentes et gestes d'entretien pour votre spa.
        </p>
      </div>

      {/* Category filter */}
      <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-category-filter">
        <button
          onClick={() => setSearchParams({})}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
            !activeCategory ? "bg-secondary text-white" : "bg-black/5 hover:bg-black/10 text-foreground"
          }`}
          data-testid="blog-filter-all"
        >
          Tout
        </button>
        {categories.map((c) => (
          <button
            key={c.category}
            onClick={() => setSearchParams({ category: c.category })}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              activeCategory === c.category
                ? "bg-secondary text-white"
                : "bg-black/5 hover:bg-black/10 text-foreground"
            }`}
            data-testid={`blog-filter-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {c.category} <span className="opacity-60">({c.count})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-16 text-sm text-muted-foreground">Chargement…</p>
      ) : posts.length === 0 ? (
        <p className="mt-16 text-sm text-muted-foreground">Aucun article pour cette catégorie.</p>
      ) : (
        <>
          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              className="mt-12 block group"
              data-testid="blog-featured-card"
            >
              <Card className="grid md:grid-cols-2 border-black/10 overflow-hidden hover:border-primary/40 transition-colors">
                <div className="aspect-[16/10] md:aspect-auto bg-gradient-to-br from-primary/20 via-secondary/30 to-slate-900 grid place-items-center">
                  {featured.cover_url ? (
                    <img src={featured.cover_url} alt={featured.title} className="h-full w-full object-cover" />
                  ) : (
                    <Waves className="h-16 w-16 text-white/60" strokeWidth={1.5} />
                  )}
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="secondary" className="rounded-full">{featured.category}</Badge>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {featured.reading_time_min} min
                    </span>
                  </div>
                  <h2 className="mt-4 font-display text-2xl lg:text-3xl font-black tracking-tight leading-tight group-hover:text-primary transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {featured.excerpt}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                    Lire l'article <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Card>
            </Link>
          )}

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-grid">
            {rest.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group"
                data-testid={`blog-post-card-${p.slug}`}
              >
                <Card className="h-full border-black/10 overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all">
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 grid place-items-center">
                    {p.cover_url ? (
                      <img src={p.cover_url} alt={p.title} className="h-full w-full object-cover" />
                    ) : (
                      <Waves className="h-10 w-10 text-white/60" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="secondary" className="rounded-full">{p.category}</Badge>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {p.reading_time_min} min
                      </span>
                    </div>
                    <h3 className="mt-3 font-display font-bold text-lg leading-tight group-hover:text-primary transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3">
                      {p.excerpt}
                    </p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* CTA */}
      <div className="mt-20 rounded-2xl bg-secondary text-white p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="eyebrow text-white/60">Diagnostic gratuit</div>
          <h3 className="mt-2 font-display text-2xl lg:text-3xl font-bold tracking-tight">
            Un problème spécifique ? Notre IA vous répond en 2 min.
          </h3>
        </div>
        <Link to="/diagnostic">
          <Button className="bg-primary hover:bg-primary/90 rounded-full h-11 px-6" data-testid="blog-cta-diagnostic">
            Lancer un diagnostic
          </Button>
        </Link>
      </div>
    </div>
  );
}
